import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Battery, Brain, Moon, Activity, Bell, Shield } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';

type Mode = 'focus' | 'immersion' | 'emergency';

interface EnergyMetrics {
  energyLevel: number;
  focusScore: number;
  restScore: number;
  sleepQuality: number;
  cycleTimeRemaining: number;
}

export function EnergyDashboard() {
  const [metrics, setMetrics] = useState<EnergyMetrics>({
    energyLevel: 85,
    focusScore: 90,
    restScore: 75,
    sleepQuality: 80,
    cycleTimeRemaining: 180 // 3 hours in minutes
  });

  const [mode, setMode] = useState<Mode>('focus');
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (!currentUser) return;

    // Load current mode
    const loadCurrentMode = async () => {
      try {
        // First, get all active modes
        const { data: activeModes, error: modesError } = await supabase
          .from('watch_modes')
          .select('*')
          .eq('user_id', currentUser.id)
          .is('ended_at', null)
          .order('started_at', { ascending: false });

        if (modesError) throw modesError;

        // If there are multiple active modes, end all but the most recent one
        if (activeModes && activeModes.length > 1) {
          const [latestMode, ...oldModes] = activeModes;
          
          // End all old modes
          await Promise.all(oldModes.map(mode => 
            supabase
              .from('watch_modes')
              .update({ ended_at: new Date().toISOString() })
              .eq('id', mode.id)
          ));

          // Set the most recent mode
          setMode(latestMode.mode as Mode);
        } else if (activeModes && activeModes.length === 1) {
          // Just set the single active mode
          setMode(activeModes[0].mode as Mode);
        }
      } catch (error) {
        console.error('Error loading current mode:', error);
      }
    };

    loadCurrentMode();

    // Update metrics every minute
    const interval = setInterval(() => {
      setMetrics(prev => ({
        ...prev,
        cycleTimeRemaining: Math.max(0, prev.cycleTimeRemaining - 1),
        energyLevel: Math.max(0, prev.energyLevel - 0.1)
      }));
    }, 60000);

    return () => clearInterval(interval);
  }, [currentUser]);

  const handleModeChange = async (newMode: Mode) => {
    if (!currentUser) return;

    setLoading(true);
    try {
      // End all active modes
      await supabase
        .from('watch_modes')
        .update({ ended_at: new Date().toISOString() })
        .eq('user_id', currentUser.id)
        .is('ended_at', null);

      // Start new mode
      const { error } = await supabase
        .from('watch_modes')
        .insert({
          user_id: currentUser.id,
          mode: newMode,
          started_at: new Date().toISOString()
        });

      if (error) throw error;

      setMode(newMode);
      
      // Create notification
      await supabase
        .from('watch_notifications')
        .insert({
          user_id: currentUser.id,
          type: 'mode_change',
          title: 'Changement de mode',
          message: `Mode ${newMode} activé`
        });

      toast({
        title: "Mode changé",
        description: `Mode ${newMode} activé avec succès.`,
      });
    } catch (error) {
      console.error('Error changing mode:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de changer de mode.",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getEnergyColor = (level: number) => {
    if (level > 70) return 'text-green-500';
    if (level > 40) return 'text-orange-500';
    return 'text-red-500';
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <h2 className="text-xl font-bold">État Énergétique</h2>
        <Battery className={`w-6 h-6 ${getEnergyColor(metrics.energyLevel)}`} />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between mb-2">
              <span className="font-medium">Niveau d'énergie</span>
              <span className={`font-bold ${getEnergyColor(metrics.energyLevel)}`}>
                {metrics.energyLevel}%
              </span>
            </div>
            <Progress value={metrics.energyLevel} className="h-2" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-4 h-4 text-purple-600" />
                <span className="font-medium">Focus</span>
              </div>
              <span className="text-2xl font-bold text-purple-600">{metrics.focusScore}%</span>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Moon className="w-4 h-4 text-blue-600" />
                <span className="font-medium">Repos</span>
              </div>
              <span className="text-2xl font-bold text-blue-600">{metrics.restScore}%</span>
            </div>
          </div>

          <div>
            <h3 className="font-medium mb-2">Mode actuel</h3>
            <div className="flex gap-2">
              <Button
                variant={mode === 'focus' ? 'default' : 'outline'}
                onClick={() => handleModeChange('focus')}
                disabled={loading}
                className="flex-1"
              >
                <Brain className="w-4 h-4 mr-2" />
                Focus
              </Button>
              <Button
                variant={mode === 'immersion' ? 'default' : 'outline'}
                onClick={() => handleModeChange('immersion')}
                disabled={loading}
                className="flex-1"
              >
                <Shield className="w-4 h-4 mr-2" />
                Immersion
              </Button>
              <Button
                variant={mode === 'emergency' ? 'destructive' : 'outline'}
                onClick={() => handleModeChange('emergency')}
                disabled={loading}
                className="flex-1"
              >
                <Bell className="w-4 h-4 mr-2" />
                Urgence
              </Button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-4 bg-gray-50 rounded-lg"
            >
              <h3 className="font-medium mb-2">Recommandations</h3>
              <ul className="space-y-2 text-sm">
                {mode === 'focus' && (
                  <>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      Période idéale pour les tâches créatives
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                      Prévoir une pause dans 45 minutes
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      Hydratation recommandée
                    </li>
                  </>
                )}
                {mode === 'immersion' && (
                  <>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-purple-500" />
                      Notifications désactivées
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      Focus maximal activé
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      Suivi énergétique renforcé
                    </li>
                  </>
                )}
                {mode === 'emergency' && (
                  <>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      Alertes prioritaires activées
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                      Monitoring continu
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-yellow-500" />
                      Support d'urgence disponible
                    </li>
                  </>
                )}
              </ul>
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-between items-center text-sm text-gray-600">
            <span>Temps restant du cycle :</span>
            <span className="font-medium">{formatTime(metrics.cycleTimeRemaining)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}